import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import {
  clearAiProviderConfiguration,
  getAiProviderConfiguration,
  testAiProviderConfiguration,
  updateAiProviderConfiguration,
} from '../services/aiPromptConfigService';
import { AI_REASONING_EFFORT_OPTIONS } from '../types/AiPromptConfig';
import type {
  AiProviderConnectionTestResult,
  AiReasoningEffort,
  AiAzureOpenAiConfiguration,
  AiDeepSeekConfiguration,
  AiOpenAiConfiguration,
  AiAnthropicConfiguration,
  AiGoogleGeminiConfiguration,
  AiMistralAiConfiguration,
  AiProviderId,
  UpdateAiAzureOpenAiConfigurationRequest,
  UpdateAiDeepSeekConfigurationRequest,
  UpdateAiOpenAiConfigurationRequest,
  UpdateAiAnthropicConfigurationRequest,
  UpdateAiGoogleGeminiConfigurationRequest,
  UpdateAiMistralAiConfigurationRequest,
} from '../types/AiPromptConfig';
import styles from './AiPromptConfigPage.module.css';

interface AiProviderConfigurationPanelProps {
  providerId: AiProviderId;
  canManage: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const getProviderTranslationKey = (id: AiProviderId): string => {
  switch (id) {
    case 'AzureOpenAI':
      return 'aiSettings.providers.azureOpenAi';
    case 'OpenAI':
      return 'aiSettings.providers.openAi';
    case 'DeepSeek':
      return 'aiSettings.providers.deepSeek';
    case 'Anthropic':
      return 'aiSettings.providers.anthropic';
    case 'GoogleGemini':
      return 'aiSettings.providers.googleGemini';
    case 'MistralAI':
      return 'aiSettings.providers.mistralAi';
    default:
      return 'aiSettings.providers.openAi';
  }
};

export const AiProviderConfigurationPanel: React.FC<AiProviderConfigurationPanelProps> = ({
  providerId,
  canManage,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<AiProviderConnectionTestResult | null>(null);
  const [isTenantConfigured, setIsTenantConfigured] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [azureForm, setAzureForm] = useState<UpdateAiAzureOpenAiConfigurationRequest>({});
  const [openAiForm, setOpenAiForm] = useState<UpdateAiOpenAiConfigurationRequest>({});
  const [deepSeekForm, setDeepSeekForm] = useState<UpdateAiDeepSeekConfigurationRequest>({});
  const [anthropicForm, setAnthropicForm] = useState<UpdateAiAnthropicConfigurationRequest>({});
  const [googleGeminiForm, setGoogleGeminiForm] = useState<UpdateAiGoogleGeminiConfigurationRequest>({});
  const [mistralAiForm, setMistralAiForm] = useState<UpdateAiMistralAiConfigurationRequest>({});

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const configuration = await getAiProviderConfiguration(providerId);
      setIsTenantConfigured(configuration.isTenantConfigured);
      setHasApiKey(configuration.hasApiKey);

      if (providerId === 'AzureOpenAI') {
        const azure = configuration as AiAzureOpenAiConfiguration;
        setAzureForm({
          endpoint: azure.endpoint,
          chatDeploymentName: azure.chatDeploymentName,
          embeddingDeploymentName: azure.embeddingDeploymentName,
          audioTranscriptionDeploymentName: azure.audioTranscriptionDeploymentName,
          apiVersion: azure.apiVersion,
        });
      } else if (providerId === 'OpenAI') {
        const openAi = configuration as AiOpenAiConfiguration;
        setOpenAiForm({
          model: openAi.model,
          embeddingModel: openAi.embeddingModel,
          temperature: openAi.temperature,
          maxOutputTokens: openAi.maxOutputTokens,
          reasoningEffort: openAi.reasoningEffort,
        });
      } else if (providerId === 'DeepSeek') {
        const deepSeek = configuration as AiDeepSeekConfiguration;
        setDeepSeekForm({
          endpoint: deepSeek.endpoint,
          model: deepSeek.model,
          thinkingEnabled: deepSeek.thinkingEnabled,
        });
      } else if (providerId === 'Anthropic') {
        const anthropic = configuration as AiAnthropicConfiguration;
        setAnthropicForm({
          model: anthropic.model,
          maxOutputTokens: anthropic.maxOutputTokens,
        });
      } else if (providerId === 'GoogleGemini') {
        const googleGemini = configuration as AiGoogleGeminiConfiguration;
        setGoogleGeminiForm({
          model: googleGemini.model,
        });
      } else if (providerId === 'MistralAI') {
        const mistralAi = configuration as AiMistralAiConfiguration;
        setMistralAiForm({
          model: mistralAi.model,
        });
      }
    } catch {
      showToast(t('aiSettings.providerConfiguration.failedToLoad', { ns: 'admin' }), 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [onClose, providerId, showToast, t]);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const runTest = async (): Promise<AiProviderConnectionTestResult | null> => {
    setTesting(true);
    try {
      const result = await testAiProviderConfiguration(providerId);
      setTestResult(result);
      return result;
    } catch {
      showToast(t('aiSettings.providerConfiguration.failedToTest', { ns: 'admin' }), 'error');
      return null;
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (providerId === 'AzureOpenAI') {
        await updateAiProviderConfiguration(providerId, azureForm);
      } else if (providerId === 'OpenAI') {
        await updateAiProviderConfiguration(providerId, openAiForm);
      } else if (providerId === 'DeepSeek') {
        await updateAiProviderConfiguration(providerId, deepSeekForm);
      } else if (providerId === 'Anthropic') {
        await updateAiProviderConfiguration(providerId, anthropicForm);
      } else if (providerId === 'GoogleGemini') {
        await updateAiProviderConfiguration(providerId, googleGeminiForm);
      } else if (providerId === 'MistralAI') {
        await updateAiProviderConfiguration(providerId, mistralAiForm);
      }

      onSaved();

      // A model that rejects the probe will also fail on inbound messages, so the panel stays
      // open with the provider's own error instead of reporting a clean save.
      const result = await runTest();
      if (result && !result.success) {
        showToast(t('aiSettings.providerConfiguration.savedButTestFailed', { ns: 'admin' }), 'error');
        return;
      }

      showToast(t('aiSettings.providerConfiguration.saved', { ns: 'admin' }), 'success');
      onClose();
    } catch {
      showToast(t('aiSettings.providerConfiguration.failedToSave', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await clearAiProviderConfiguration(providerId);
      showToast(t('aiSettings.providerConfiguration.reset', { ns: 'admin' }), 'success');
      onSaved();
      onClose();
    } catch {
      showToast(t('aiSettings.providerConfiguration.failedToReset', { ns: 'admin' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const providerTitle = t(`${getProviderTranslationKey(providerId)}.title`, { ns: 'admin' });

  return (
    <div className={styles.providerConfigOverlay} role="presentation" onClick={onClose}>
      <div
        className={styles.providerConfigPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="provider-config-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.providerConfigHeader}>
          <div>
            <h3 id="provider-config-title" className={styles.providerConfigTitle}>{providerTitle}</h3>
            <p className={styles.providerConfigDescription}>
              {t('aiSettings.providerConfiguration.description', { ns: 'admin' })}
            </p>
          </div>
          <button
            type="button"
            className={styles.providerConfigClose}
            onClick={onClose}
            aria-label={t('close', { ns: 'common', defaultValue: 'Close' })}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div className={styles.toolPolicyLoading}>
            {t('aiSettings.providerConfiguration.loading', { ns: 'admin' })}
          </div>
        ) : (
          <>
            <div className={styles.providerConfigBadges}>
              {isTenantConfigured ? (
                <span className={styles.policyConfigured}>{t('aiSettings.tenantOverride', { ns: 'admin' })}</span>
              ) : (
                <span className={styles.policyBadgeAllowed}>
                  {t('aiSettings.providerConfiguration.usingDeploymentDefaults', { ns: 'admin' })}
                </span>
              )}
              <span className={styles.policyBadgeAllowed}>
                {hasApiKey
                  ? t('aiSettings.providerConfiguration.apiKeyConfigured', { ns: 'admin' })
                  : t('aiSettings.providerConfiguration.apiKeyMissing', { ns: 'admin' })}
              </span>
            </div>

            {providerId === 'AzureOpenAI' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.endpoint', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={azureForm.endpoint ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAzureForm((current) => ({ ...current, endpoint: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={azureForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setAzureForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.chatDeploymentName', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={azureForm.chatDeploymentName ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAzureForm((current) => ({ ...current, chatDeploymentName: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.embeddingDeploymentName', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={azureForm.embeddingDeploymentName ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAzureForm((current) => ({ ...current, embeddingDeploymentName: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.audioTranscriptionDeploymentName', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={azureForm.audioTranscriptionDeploymentName ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAzureForm((current) => ({ ...current, audioTranscriptionDeploymentName: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiVersion', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={azureForm.apiVersion ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAzureForm((current) => ({ ...current, apiVersion: event.target.value }))}
                  />
                </label>
              </div>
            ) : null}

            {providerId === 'OpenAI' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={openAiForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setOpenAiForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.model', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={openAiForm.model ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setOpenAiForm((current) => ({ ...current, model: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.embeddingModel', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={openAiForm.embeddingModel ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setOpenAiForm((current) => ({ ...current, embeddingModel: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.temperature', { ns: 'admin' })}</span>
                  <input
                    className={styles.numberInput}
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={openAiForm.temperature ?? 0.2}
                    disabled={!canManage || saving}
                    onChange={(event) => setOpenAiForm((current) => ({
                      ...current,
                      temperature: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.maxOutputTokens', { ns: 'admin' })}</span>
                  <input
                    className={styles.numberInput}
                    type="number"
                    min="1"
                    value={openAiForm.maxOutputTokens ?? 1024}
                    disabled={!canManage || saving}
                    onChange={(event) => setOpenAiForm((current) => ({
                      ...current,
                      maxOutputTokens: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.reasoningEffort', { ns: 'admin' })}</span>
                  <select
                    className={styles.select}
                    value={openAiForm.reasoningEffort ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setOpenAiForm((current) => ({
                      ...current,
                      reasoningEffort: event.target.value as AiReasoningEffort,
                    }))}
                  >
                    {AI_REASONING_EFFORT_OPTIONS.map((option) => (
                      <option key={option || 'auto'} value={option}>
                        {t(`aiSettings.providerConfiguration.reasoningEffortOptions.${option || 'auto'}`, { ns: 'admin' })}
                      </option>
                    ))}
                  </select>
                  <span className={styles.fieldHelp}>
                    {t('aiSettings.providerConfiguration.reasoningEffortHelp', { ns: 'admin' })}
                  </span>
                </label>
              </div>
            ) : null}

            {providerId === 'DeepSeek' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={deepSeekForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setDeepSeekForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.endpoint', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={deepSeekForm.endpoint ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setDeepSeekForm((current) => ({ ...current, endpoint: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.model', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={deepSeekForm.model ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setDeepSeekForm((current) => ({ ...current, model: event.target.value }))}
                  />
                </label>
                <label className={styles.toggleRow}>
                  <div className={styles.toggleInfo}>
                    <span className={styles.toggleTitle}>
                      {t('aiSettings.providerConfiguration.thinkingEnabled', { ns: 'admin' })}
                    </span>
                    <span className={styles.toggleHelp}>
                      {t('aiSettings.providerConfiguration.thinkingEnabledHelp', { ns: 'admin' })}
                    </span>
                  </div>
                  <label className={styles.toggle}>
                    <input
                      type="checkbox"
                      checked={Boolean(deepSeekForm.thinkingEnabled)}
                      disabled={!canManage || saving}
                      onChange={(event) => setDeepSeekForm((current) => ({
                        ...current,
                        thinkingEnabled: event.target.checked,
                      }))}
                    />
                    <span className={styles.toggleSlider} />
                  </label>
                </label>
              </div>
            ) : null}

            {providerId === 'Anthropic' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={anthropicForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setAnthropicForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.model', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={anthropicForm.model ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setAnthropicForm((current) => ({ ...current, model: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.maxOutputTokens', { ns: 'admin' })}</span>
                  <input
                    className={styles.numberInput}
                    type="number"
                    min="1"
                    value={anthropicForm.maxOutputTokens ?? 1024}
                    disabled={!canManage || saving}
                    onChange={(event) => setAnthropicForm((current) => ({
                      ...current,
                      maxOutputTokens: Number(event.target.value),
                    }))}
                  />
                </label>
              </div>
            ) : null}

            {providerId === 'GoogleGemini' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={googleGeminiForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setGoogleGeminiForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.model', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={googleGeminiForm.model ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setGoogleGeminiForm((current) => ({ ...current, model: event.target.value }))}
                  />
                </label>
              </div>
            ) : null}

            {providerId === 'MistralAI' ? (
              <div className={styles.providerConfigGrid}>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.apiKey', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    type="password"
                    value={mistralAiForm.apiKey ?? ''}
                    disabled={!canManage || saving}
                    placeholder={hasApiKey ? t('aiSettings.providerConfiguration.apiKeyPlaceholder', { ns: 'admin' }) : ''}
                    autoComplete="new-password"
                    onChange={(event) => setMistralAiForm((current) => ({ ...current, apiKey: event.target.value }))}
                  />
                </label>
                <label className={styles.fieldGroup}>
                  <span className={styles.fieldLabel}>{t('aiSettings.providerConfiguration.model', { ns: 'admin' })}</span>
                  <input
                    className={styles.textInput}
                    value={mistralAiForm.model ?? ''}
                    disabled={!canManage || saving}
                    onChange={(event) => setMistralAiForm((current) => ({ ...current, model: event.target.value }))}
                  />
                </label>
              </div>
            ) : null}

            {testResult ? (
              <div
                className={`${styles.providerTestResult} ${testResult.success ? styles.providerTestSuccess : styles.providerTestError}`}
                role="status"
              >
                <strong>
                  {testResult.success
                    ? t('aiSettings.providerConfiguration.testSucceeded', { ns: 'admin', model: testResult.model })
                    : t('aiSettings.providerConfiguration.testFailed', { ns: 'admin', model: testResult.model })}
                </strong>
                {testResult.error ? <p>{testResult.error}</p> : null}
              </div>
            ) : null}

            {canManage ? (
              <div className={styles.toolPolicyActions}>
                <button
                  type="button"
                  className={styles.btnPolicySave}
                  onClick={handleSave}
                  disabled={saving || testing}
                >
                  {saving ? t('saving', { ns: 'common' }) : t('aiSettings.providerConfiguration.save', { ns: 'admin' })}
                </button>
                <button
                  type="button"
                  className={styles.btnProviderTest}
                  onClick={() => void runTest()}
                  disabled={saving || testing}
                >
                  {testing
                    ? t('aiSettings.providerConfiguration.testing', { ns: 'admin' })
                    : t('aiSettings.providerConfiguration.test', { ns: 'admin' })}
                </button>
                {isTenantConfigured ? (
                  <button
                    type="button"
                    className={styles.btnReset}
                    onClick={handleReset}
                    disabled={saving || testing}
                  >
                    {t('aiSettings.providerConfiguration.reset', { ns: 'admin' })}
                  </button>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};
